import {
  Box,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { DownArrow, UpArrow } from "~/icons/percentageChangeArrows";
import React, { memo, useMemo, useRef } from "react";
import {
  formatCurrencyRounded,
  getConvertedValue,
} from "~/utils/currencyUtils";

import { DATETIME_PERIODS } from "~/store/utils/dateTimeUtils";
import { InlineIconButton } from "~/icons/inlineIconButton";
import LargeNumber from "./largeNumber";
import { LightHeading } from "./styledTypography";
import StatusText from "../typography/status";
import { VendorRevenueType } from "~/store/mystore/vendor.redux";
import { getGrowthStatus } from "../table/cells/valueAndGrowthCell";
import { getPercentageDifference } from "~/utils/salesUtils";
import { numberWithCommas } from "~/utils/utils";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

interface TotalSalesProps {
  current: number;
  prior?: number;
  currency?: string;
  isVendorStore?: boolean;
  currentCurrency: string;
  currentPeriod: DATETIME_PERIODS;
  conditionalFormatting?: boolean;
  customTitle?: string;
  comparisonSubtitle?: string;
  currentSubtitle?: string;
  vendorRevenueType?: VendorRevenueType;
  refundedSales?: number | null;
  refundSalesTooltip?: string;
  cancelledSalesTooltip?: string;
}

export const TotalSales = memo<TotalSalesProps>(function TotalSales({
  current,
  prior,
  currency = "AUD",
  isVendorStore,
  currentCurrency,
  currentPeriod,
  conditionalFormatting,
  customTitle,
  comparisonSubtitle,
  currentSubtitle,
  vendorRevenueType = VendorRevenueType.OrderedRevenue,
  refundedSales,
  refundSalesTooltip,
  cancelledSalesTooltip,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const currencyRates = useTypedSelector((state) =>
    state.globalVar ? state.globalVar.currencyRates : []
  );

  /** If totalSales is a large number, our default font size can overflow the container.
    LargeNumber will recalculate the font size dynamically to prevent this from happening */
  const headingContainerRef = useRef<HTMLDivElement>(null);

  const { totalSales, lastPeriodSales, growth, totalRefundedSales } =
    useMemo(() => {
      const sales = current || "-";
      const lastPeriodSales = prior || "-";

      const convertedTotalSales = formatCurrencyRounded(
        sales,
        currencyRates,
        currency,
        currentCurrency
      );

      const convertedLastPeriodSales = formatCurrencyRounded(
        lastPeriodSales,
        currencyRates,
        currency,
        currentCurrency
      );

      const growthValue = getPercentageDifference(sales, lastPeriodSales);

      const convertedTotalRefundSales = refundedSales
        ? formatCurrencyRounded(
            refundedSales,
            currencyRates,
            currency,
            currentCurrency
          )
        : null;

      return {
        totalSales: convertedTotalSales,
        lastPeriodSales: convertedLastPeriodSales,
        growth: growthValue,
        totalRefundedSales: convertedTotalRefundSales,
      };
    }, [
      currentCurrency,
      currencyRates,
      current,
      prior,
      currency,
      refundedSales,
    ]);

  const status = getGrowthStatus(growth);

  return (
    <Grid
      container
      alignItems="center"
      justifyContent={smDown ? "center" : "flex-start"}
      spacing={1}
      id="total-sales-wrapper"
    >
      <Grid item xs={6} md={12}>
        <Grid container wrap="nowrap" alignItems="center">
          <Typography variant="h3">
            {t(
              customTitle?.length
                ? customTitle
                : isVendorStore
                ? vendorRevenueType === VendorRevenueType.OrderedRevenue
                  ? "dashboardWidget.totalOrderedRevenue"
                  : "dashboardWidget.totalShippedRevenue"
                : "dashboardWidget.salesPerformance.totalSales"
            )}
          </Typography>
          {cancelledSalesTooltip && cancelledSalesTooltip?.trim() !== "" ? (
            <Tooltip title={cancelledSalesTooltip}>
              <InlineIconButton />
            </Tooltip>
          ) : undefined}
        </Grid>

        {currentSubtitle && (
          <Typography variant="body1" color="textSecondary">
            {currentSubtitle}
          </Typography>
        )}
      </Grid>
      <Grid
        container
        item
        wrap="wrap"
        alignItems="center"
        xs={6}
        md={12}
        ref={headingContainerRef}
      >
        <LargeNumber
          variant="h1"
          headingContainerRef={headingContainerRef}
          value={numberWithCommas(totalSales)}
        />
        <LightHeading variant="h3" color="textSecondary">
          {currentCurrency}
        </LightHeading>
      </Grid>
      {totalRefundedSales && refundSalesTooltip ? (
        <>
          {/* Spacer Start */}
          <Grid item xs={12}></Grid>
          {/* Spacer End */}
          <Grid container item wrap="nowrap" alignItems="center" xs={6} md={12}>
            <Typography variant="h3">
              {t("dashboardWidget.salesPerformance.totalRefund")}
            </Typography>
            <Tooltip title={refundSalesTooltip}>
              <InlineIconButton />
            </Tooltip>
          </Grid>
          <Grid item xs={6} md={12}>
            <LargeNumber
              variant="h2"
              headingContainerRef={headingContainerRef}
              value={numberWithCommas(totalRefundedSales)}
            />
          </Grid>
        </>
      ) : undefined}
      {/* Spacer Start */}
      <Grid item xs={12}></Grid>
      {/* Spacer End */}
      <Grid item xs={6} md={12}>
        <Typography variant="h3">
          {t("dashboardWidget.salesPerformance.priorPeriod")}
        </Typography>
        {comparisonSubtitle && (
          <Typography variant="body1" color="textSecondary">
            {comparisonSubtitle}
          </Typography>
        )}
      </Grid>
      <Grid container item wrap="wrap" alignItems="center" xs={6} md={12}>
        <LargeNumber
          variant="h2"
          headingContainerRef={headingContainerRef}
          value={numberWithCommas(lastPeriodSales)}
        />
        <Tooltip title="Percentage change compared to the equivalent prior period">
          <StatusText
            variant="h5"
            status={conditionalFormatting === false ? undefined : status}
          >
            {status === "success" && (
              <UpArrow fontSize="inherit" status="success" />
            )}
            {status === "error" && (
              <DownArrow fontSize="inherit" status="error" />
            )}
            {isNaN(Number(growth)) ? growth : Math.abs(Number(growth))}
            {!isNaN(Number(growth)) && "%"}
          </StatusText>
        </Tooltip>
      </Grid>
    </Grid>
  );
});

export default TotalSales;
